import {
  createAsyncThunk,
  createSlice,
  Draft,
  PayloadAction
} from '@reduxjs/toolkit';

import { apiService } from '../../services/api-service';
import { QueueStatus } from '../../types/api';
import { ChatQueue, SubTopic, Topic } from '../../types/async-config';
import { boolify } from '../../utils/type-conversion';
import { ShortNameAndEntrance } from './config';

export interface TopicSelectionState {
  hasRobot: boolean;
  hasTopics: boolean;
  hasSubTopics: boolean;
  customerType?: ChatQueue;
  mainTopic?: Topic;
  subTopic?: SubTopic;
  popupAuthentication: boolean;
  selectedEntrance?: string;
  selectedErrand?: string;
  selectedSurvey?: string;
  authenticate?: string;
  shortnameAvailability?: boolean;
}

const initialState: TopicSelectionState = {
  hasRobot: false,
  hasTopics: false,
  hasSubTopics: false,
  shortnameAvailability: undefined,
  popupAuthentication: false
};

function commonSelect(
  state: Draft<TopicSelectionState>,
  selection: ChatQueue | Topic | SubTopic
) {
  state.selectedEntrance = selection.entrance;
  state.selectedErrand = selection.errand;
  state.selectedSurvey = selection.survey;
  state.authenticate = selection.authenticate;
  state.popupAuthentication = boolify(selection.popupAuthentication);
}

function clearSelection(state: Draft<TopicSelectionState>) {
  state.subTopic = undefined;
  state.selectedEntrance = undefined;
  state.selectedErrand = undefined;
  state.selectedSurvey = undefined;
  state.authenticate = undefined;
}

export const fetchShortnameAvailability = createAsyncThunk<
  QueueStatus,
  string | (string | undefined)[]
>('shortNameAvailability/request', (shortNames) =>
  apiService.getChatQueueAvailability(shortNames)
);

export const fetchShortnameAndEntranceAvailability = createAsyncThunk<
  QueueStatus,
  ShortNameAndEntrance | (ShortNameAndEntrance | undefined)[]
>('shortNameAndEntranceAvailability/request', (shortNameAndEntrance) =>
  apiService.getChatQueueEntranceAvailability(shortNameAndEntrance)
);

const topicSelection = createSlice({
  name: 'topicSelection',
  initialState,
  reducers: {
    customerTypeSelect(state, action: PayloadAction<ChatQueue>) {
      state.hasRobot = 'robot' in action.payload;
      state.customerType = action.payload;
      state.hasTopics = !!action.payload.topics?.length;
      commonSelect(state, action.payload);
    },
    revertCustomerTypeSelection() {
      return initialState;
    },
    mainTopicSelect(state, action: PayloadAction<Topic>) {
      state.mainTopic = action.payload;
      if (action.payload.subTopics?.length) {
        state.hasSubTopics = true;
      } else {
        commonSelect(state, action.payload);
      }
    },
    subTopicSelect(state, action: PayloadAction<SubTopic>) {
      state.subTopic = action.payload;
      commonSelect(state, action.payload);
    },
    revertSubTopicSelection: clearSelection,
    revertMainTopicSelection(state) {
      state.hasSubTopics = false;
      state.mainTopic = undefined;
      clearSelection(state);
    },
    initializeTopicSelection(
      state,
      action: PayloadAction<Partial<TopicSelectionState> | undefined>
    ) {
      return action.payload
        ? {
            ...state,
            ...action.payload
          }
        : initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      // .addCase(fetchShortnameAvailability.pending, (state) => {
      //   state.shortnameAvailability = undefined;
      // })
      // .addCase(fetchShortnameAvailability.fulfilled, (state, action) => {
      //   state.shortnameAvailability = action.payload.available;
      // })
      .addCase(fetchShortnameAndEntranceAvailability.pending, (state) => {
        state.shortnameAvailability = undefined;
      })
      .addCase(
        fetchShortnameAndEntranceAvailability.fulfilled,
        (state, action) => {
          state.shortnameAvailability = action.payload.available;
        }
      );
  }
});

export const {
  customerTypeSelect,
  mainTopicSelect,
  subTopicSelect,
  revertCustomerTypeSelection,
  revertMainTopicSelection,
  revertSubTopicSelection,
  initializeTopicSelection
} = topicSelection.actions;

export default topicSelection.reducer;
